<template>
	<AccessibleSection
		name="hiddengroups"
		class="settings-wrapper hidden-group-list-wrapper"
		:tabOrder="tabOrder"
	>
		<div class="fixed-top">
			<div class="page-header">
				<BackToSettings />
				<h2 class="title header-30" v-t="'settings_option_hidden_groups'" />
			</div>

			<div
				class="hidden-chats-description"
				v-t="'settings_hidden_groups_description'"
			/>

			<div class="hidden-pills">
				<button class="hidden-pill" @click="activeModal = 'group'">
					<div class="hidden-pill-icon-wrapper">
						<UserGroupIcon type="solid" />
					</div>
					<span class="hidden-pill-label">{{
						$t('settings_hidden_groups_view_hidden_groups')
					}}</span>
					<span v-if="hiddenGroupCount !== null" class="hidden-pill-count">{{
						hiddenGroupCount
					}}</span>
					<ChevronIcon right type="solid" class="hidden-pill-chevron" />
				</button>
				<button class="hidden-pill" @click="activeModal = 'thread'">
					<div class="hidden-pill-icon-wrapper thread-icon">
						<ReelIcon type="solid" />
					</div>
					<span class="hidden-pill-label">{{
						$t('settings_hidden_groups_view_hidden_threads')
					}}</span>
					<span v-if="hiddenThreadCount !== null" class="hidden-pill-count">{{
						hiddenThreadCount
					}}</span>
					<ChevronIcon right type="solid" class="hidden-pill-chevron" />
				</button>
				<button class="hidden-pill" @click="activeModal = 'dm'">
					<div class="hidden-pill-icon-wrapper">
						<CommentIcon type="solid" />
					</div>
					<span class="hidden-pill-label">{{
						$t('settings_hidden_groups_view_hidden_dms')
					}}</span>
					<span v-if="hiddenDmCount !== null" class="hidden-pill-count">{{
						hiddenDmCount
					}}</span>
					<ChevronIcon right type="solid" class="hidden-pill-chevron" />
				</button>
			</div>

			<div
				class="hidden-chats-tip"
				v-t="'settings_hidden_groups_auto_hide_hint_web'"
			/>

			<div class="hidden-chats-divider" />

			<div class="auto-hide-section">
				<h3
					class="auto-hide-title"
					v-t="'settings_hidden_groups_auto_hide_title'"
				/>
				<div class="auto-hide-options">
					<div class="auto-hide-row">
						<span
							class="auto-hide-label"
							v-t="'settings_hidden_groups_auto_hide_groups'"
						/>
						<Dropdown
							:modelValue="autoHideGroupMins"
							:options="optionsWithFallback(autoHideGroupMins)"
							:loading="savingField === 'autoHideGroupMins'"
							:collisionHeight="350"
							global
							optionsClass="hidden-group-list-dropdown"
							@change="updateAutoHide('autoHideGroupMins', $event)"
						/>
					</div>
					<div class="auto-hide-row">
						<span
							class="auto-hide-label"
							v-t="'settings_hidden_groups_auto_hide_threads'"
						/>
						<Dropdown
							:modelValue="autoHideThreadMins"
							:options="optionsWithFallback(autoHideThreadMins)"
							:loading="savingField === 'autoHideThreadMins'"
							:collisionHeight="350"
							global
							optionsClass="hidden-group-list-dropdown"
							@change="updateAutoHide('autoHideThreadMins', $event)"
						/>
					</div>
					<div class="auto-hide-row">
						<span
							class="auto-hide-label"
							v-t="'settings_hidden_groups_auto_hide_dms'"
						/>
						<Dropdown
							:modelValue="autoHideDmMins"
							:options="optionsWithFallback(autoHideDmMins)"
							:loading="savingField === 'autoHideDmMins'"
							:collisionHeight="350"
							global
							optionsClass="hidden-group-list-dropdown"
							@change="updateAutoHide('autoHideDmMins', $event)"
						/>
					</div>
				</div>
				<div
					class="auto-hide-description"
					v-t="'settings_hidden_groups_auto_hide_description'"
				/>
			</div>
		</div>

		<HiddenItemsModal
			v-if="activeModal"
			:type="activeModal"
			@close="activeModal = null"
			@unhide="onItemUnhidden"
		/>
	</AccessibleSection>
</template>

<script>
import store, { getMyUser, getMyUserId } from '@/store'
import { Dropdown } from '@/components/common'
import { AccessibleSection } from '@/components/common/Accessible'
import BackToSettings from './BackToSettings'
import HiddenItemsModal from './HiddenItemsModal'
import { updateUser, fetchMembershipCounts } from '@/api/user'
import { getAutoHideOptionsWithFallback } from '@/helpers/autoHideOptions'
import { ChevronIcon, UserGroupIcon, ReelIcon, CommentIcon } from '@/icons'

export default {
	components: {
		BackToSettings,
		Dropdown,
		ChevronIcon,
		UserGroupIcon,
		ReelIcon,
		CommentIcon,
		AccessibleSection,
		HiddenItemsModal,
	},
	data() {
		return {
			store,
			autoHideGroupMins: getMyUser()?.auto_hide_group_mins ?? 0,
			autoHideThreadMins: getMyUser()?.auto_hide_thread_mins ?? 0,
			autoHideDmMins: getMyUser()?.auto_hide_dm_mins ?? 0,
			savingField: null,
			activeModal: null,
			hiddenGroupCount: null,
			hiddenThreadCount: null,
			hiddenDmCount: null,
		}
	},

	async created() {
		try {
			const counts = await fetchMembershipCounts(getMyUserId())
			this.hiddenGroupCount = counts.hidden_group_count
			this.hiddenThreadCount = counts.hidden_thread_count
			this.hiddenDmCount = counts.hidden_dm_count
		} catch (error) {
			console.error(error)
		}
	},

	computed: {
		tabOrder() {
			return [
				{ css: '.page-header .pronto-btn', clickOnEnter: true },
				{
					all: '.hidden-pill',
					arrows: true,
					clickOnEnter: true,
				},
				{
					all: '.auto-hide-row .dropdown-selected',
					clickOnEnter: true,
					arrows: true,
				},
			]
		},
	},

	methods: {
		optionsWithFallback(value) {
			return getAutoHideOptionsWithFallback(this.$t, value)
		},
		onItemUnhidden(type) {
			const countMap = {
				group: 'hiddenGroupCount',
				thread: 'hiddenThreadCount',
				dm: 'hiddenDmCount',
			}
			const field = countMap[type]
			if (field && this[field] !== null && this[field] > 0) {
				this[field]--
			}
		},
		async updateAutoHide(field, value) {
			const apiFieldMap = {
				autoHideGroupMins: 'auto_hide_group_mins',
				autoHideThreadMins: 'auto_hide_thread_mins',
				autoHideDmMins: 'auto_hide_dm_mins',
			}
			const prevValue = this[field]
			this[field] = value
			this.savingField = field
			try {
				// "Never" is stored as null on the server, but the Dropdown component
				// treats null as "no selection," so we use 0 internally and convert back here.
				await updateUser({ [apiFieldMap[field]]: value || null })
				this.$alert.success(this.$t('common_confirmation_updated_nocheck'))
			} catch (error) {
				this[field] = prevValue
				this.$alert.error(error)
			}
			this.savingField = null
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.hidden-group-list-wrapper {
	position: relative;
	padding: 0 50px;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	height: 100%;

	.title {
		color: var(--neutral-800);
		margin: 0;
	}
}

.page-header {
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	padding: 30px 0 0;
}

.page-header :deep(.pronto-btn) {
	position: absolute;
	left: 0;
	top: 30px;
}

.fixed-top {
	flex-shrink: 0;
}

.hidden-chats-description,
.hidden-chats-tip,
.auto-hide-description {
	max-width: 800px;
	font-family: Avenir-Medium;
	color: var(--neutral-600);
}

.hidden-chats-description {
	margin: 30px auto 0;
	font-size: 13px;
}

.hidden-chats-tip {
	margin: 12px auto 0;
	font-size: 13px;
}

.auto-hide-description {
	margin: 12px 0 0;
	font-size: 12px;
}

.auto-hide-section {
	max-width: 800px;
	margin: 24px auto 0;
}

.auto-hide-title {
	font-family: Avenir-Heavy;
	font-size: 20px;
	color: var(--neutral-800);
	margin: 0 0 16px;
}

.auto-hide-options {
	border: 1px solid var(--neutral-200);
	border-radius: 12px;
	overflow: hidden;
	background: var(--neutral-100);
}

.auto-hide-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;

	&:not(:last-child) {
		border-bottom: 1px solid var(--neutral-200);
	}

	.dropdown {
		width: 160px;
	}
}

.auto-hide-label {
	font-family: Avenir-Medium;
	font-size: 15px;
	color: var(--neutral-800);
}

.hidden-chats-divider {
	max-width: 800px;
	margin: 30px auto 0;
	border-top: 1px solid var(--neutral-200);
}

.hidden-pills {
	max-width: 800px;
	margin: 16px auto 0;
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.hidden-pill {
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	padding: 12px 16px;
	border: 1px solid var(--neutral-200);
	border-radius: 12px;
	background: var(--neutral-100);
	cursor: pointer;
	transition: background 150ms ease, border-color 150ms ease;

	&:hover {
		background: var(--neutral-200);
		border-color: var(--neutral-300);
	}
}

.hidden-pill-icon-wrapper {
	width: 36px;
	height: 36px;
	border-radius: 8px;
	background: var(--neutral-0);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	color: var(--neutral-500);
	font-size: 16px;

	.pronto-icon {
		font-size: 19px;
	}

	&.thread-icon .pronto-icon {
		transform: rotate(30deg);
	}
}

.hidden-pill-label {
	flex: 1;
	text-align: left;
	font-family: Avenir-Medium;
	font-size: 15px;
	color: var(--neutral-800);
}

.hidden-pill-count {
	font-family: Avenir-Roman;
	font-size: 15px;
	color: var(--neutral-800);
}

.hidden-pill-chevron {
	font-size: 15px;
	color: var(--neutral-700);
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.hidden-group-list-wrapper {
		.hidden-pill {
			background: var(--neutral-0);
			border-color: var(--neutral-300);

			&:hover {
				background: var(--neutral-200);
			}
		}
		.hidden-pill-icon-wrapper {
			background: var(--neutral-100);
		}
	}

	.dropdown-options-container.hidden-group-list-dropdown.open,
	.dropdown-options-container.hidden-group-list-dropdown.transition {
		background: var(--neutral-100) !important;
		border-color: var(--blue-300) !important;
	}
}

.dropdown-options-container.hidden-group-list-dropdown {
	&.open {
		max-height: 350px;
	}
	&.scrollable .scroller {
		max-height: 350px;
	}
}
</style>
